import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, FileText, Loader2, Minus, X } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Boxes } from 'lucide-react'
import { startConceptBuild } from '@/lib/api/concepts/build-concepts'
import {
  cancelIngest,
  checkHashes,
  fileHash,
  getIngestStatus,
  uploadFiles,
} from '@/lib/api/documents/ingest'
import { apiErrorMessage } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import {
  abortRun,
  beginRun,
  endRun,
  getUploads,
  setUploads as setStoreUploads,
  clearUploads,
  subscribe as subscribeUploads,
} from '@/lib/ingest-store'
import type { IngestFile, IngestStatus } from '@/types/ingest'

/** A file the browser still holds — not yet stored, so it has no document row. */
export interface UploadRow {
  key: string
  name: string
  status: 'checking' | 'waiting' | 'sending' | 'skipped' | 'failed'
  percentage: number
  message?: string
  /** Title of the document this file duplicates, when skipped. */
  duplicateOf?: string
}

/**
 * Bulk ingest state for one collection.
 *
 * A hook rather than component-local state because three places need the same
 * view of it: the toolbar button and the empty-state drop target both start a
 * run, and the document list renders rows for files that are still in flight.
 *
 * Two sources of truth, deliberately. Files being *transferred* exist only in
 * this browser, so they are local state. Files being *processed* exist on the
 * server and are polled — that run is a detached job keyed by collection, so it
 * shows up in every tab, survives navigating away, and survives a reload.
 */
export function useIngest(collectionId: number | null, domains?: string) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const wasRunning = useRef(false)
  // The rows live outside React so that leaving this view — including the
  // URL rewrite a language switch performs — cannot unmount them mid-transfer.
  const uploads = useSyncExternalStore(subscribeUploads, () => getUploads(collectionId))
  const setUploads = useCallback(
    (u: UploadRow[] | ((cur: UploadRow[]) => UploadRow[])) => {
      if (collectionId !== null) setStoreUploads(collectionId, u)
    },
    [collectionId],
  )

  useEffect(() => {
    wasRunning.current = false
  }, [collectionId])

  // Deliberately no abort on unmount: navigating away must not cancel an
  // upload. Only the cancel button does.

  const status = useQuery<IngestStatus>({
    queryKey: ['ingest', collectionId],
    queryFn: () => getIngestStatus(collectionId!),
    enabled: collectionId !== null,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 1500 : false),
    refetchOnWindowFocus: true,
  })

  const sending = uploads.some(
    (u) => u.status === 'checking' || u.status === 'waiting' || u.status === 'sending',
  )
  const running = status.data?.status === 'running'

  // Per-document processing state, so a row in the list can show what is
  // happening to it rather than just that it is unprocessed.
  const byDocument = useMemo(() => {
    const m = new Map<number, IngestFile>()
    for (const f of status.data?.files ?? []) {
      if (f.document_id != null) m.set(f.document_id, f)
    }
    return m
  }, [status.data])

  useEffect(() => {
    const d = status.data
    if (!d) return
    if (d.status === 'running') {
      wasRunning.current = true
      return
    }
    if (!wasRunning.current) return
    wasRunning.current = false
    const r = d.result
    if (d.status === 'error') {
      toast.error(t('ingest.failed'), { description: d.message ?? '' })
    } else if (r) {
      const parts = [t('ingest.summaryDone', { count: r.done })]
      if (r.failed) parts.push(t('ingest.summaryFailed', { count: r.failed }))
      const body = { description: parts.join(' · ') }
      if (r.failed) toast.warning(t('ingest.finishedWithErrors'), body)
      else toast.success(t('ingest.finished'), body)
    }
    qc.invalidateQueries({ queryKey: ['documents'] })
    qc.invalidateQueries({ queryKey: ['terms'] })
    qc.invalidateQueries({ queryKey: ['domains'] })
    qc.invalidateQueries({ queryKey: ['collections'] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.data])

  const start = useCallback(
    async (files: File[]) => {
      if (collectionId === null || !files.length) return
      const signal = beginRun(collectionId).signal
      const stamp = Date.now()
      // Every dropped file gets a row before a single byte is sent, so the
      // list answers "did it take them?" immediately rather than after the
      // whole transfer.
      const rows: UploadRow[] = files.map((f, i) => ({
        key: `${stamp}-${i}-${f.name}`,
        name: f.name,
        status: 'checking',
        percentage: 0,
      }))
      setUploads(rows)

      // Identify the files first, so duplicates are never uploaded at all.
      // Re-dropping a folder of thirty to add two is the normal way to use
      // this; without the check that means transferring twenty-eight files in
      // order to be told they were already here.
      const digests: (string | null)[] = []
      for (const f of files) {
        if (signal.aborted) return
        digests.push(await fileHash(f))
      }
      const known = await checkHashes(
        collectionId,
        digests.filter((h): h is string => !!h),
      )

      const seen = new Map<string, string>()
      const skips: { name: string; of: string }[] = []
      const toSend: { file: File; row: number }[] = []
      files.forEach((f, i) => {
        const h = digests[i]
        // Two copies of the same file inside one drop: the first goes, the
        // rest match it. The server would catch this too, but only after the
        // upload, and it would name a document that did not exist a moment ago.
        const dup = h ? (known[h] ?? seen.get(h)) : undefined
        if (dup !== undefined) {
          skips.push({ name: f.name, of: dup })
          rows[i] = { ...rows[i], status: 'skipped', duplicateOf: dup }
        } else {
          if (h) seen.set(h, f.name)
          toSend.push({ file: f, row: i })
        }
      })
      // Say it now, not when the batch ends — the point of checking first is
      // that the answer is available before anything is transferred.
      if (skips.length) {
        toast.info(
          skips.length === files.length
            ? t('ingest.allDuplicates', { count: skips.length })
            : t('ingest.skippedDuplicates', { count: skips.length }),
          {
            description:
              skips.length === 1
                ? t('ingest.duplicateOf', { title: skips[0].of })
                : skips.map((s) => s.name).join(', '),
          },
        )
      }
      rows.forEach((r, i) => {
        if (r.status === 'checking') {
          rows[i] = { ...r, status: i === toSend[0]?.row ? 'sending' : 'waiting' }
        }
      })
      setUploads([...rows])
      if (!toSend.length) {
        setUploads(rows.filter((u) => u.status === 'skipped'))
        return
      }

      await uploadFiles(
        collectionId,
        toSend.map((x) => x.file),
        {
          domains,
          signal,
          onProgress: (i, pct) => {
            const row = toSend[i].row
            setUploads((cur) =>
              cur.map((u, j) => (j === row ? { ...u, status: 'sending', percentage: pct } : u)),
            )
          },
          onFileDone: (outcome, i) => {
            const row = toSend[i].row
            const next = toSend[i + 1]?.row
            setUploads((cur) =>
              cur.map((u, j) => {
                if (j === row) {
                  // A stored file now has a real document row; the ghost goes.
                  if (outcome.status === 'queued') {
                    return { ...u, status: 'sending', percentage: 100 }
                  }
                  return {
                    ...u,
                    status: outcome.status,
                    message: outcome.message,
                    duplicateOf: outcome.duplicate_of,
                  }
                }
                if (j === next && u.status === 'waiting') return { ...u, status: 'sending' }
                return u
              }),
            )
            if (outcome.status === 'queued') {
              // The document exists now — show it, and pick up the queue state.
              qc.invalidateQueries({ queryKey: ['documents'] })
              qc.invalidateQueries({ queryKey: ['ingest', collectionId] })
            }
          },
        },
      )

      // Keep only rows that never became documents, so the failures and skips
      // stay readable while the successes hand over to real list rows.
      setUploads((cur) => cur.filter((u) => u.status === 'failed' || u.status === 'skipped'))
      endRun(collectionId)
      qc.invalidateQueries({ queryKey: ['documents'] })
      qc.invalidateQueries({ queryKey: ['ingest', collectionId] })
    },
    [collectionId, domains, qc, setUploads, t],
  )

  const stop = useCallback(async () => {
    if (collectionId === null) return
    abortRun(collectionId)
    setUploads((cur) => cur.filter((u) => u.status === 'failed' || u.status === 'skipped'))
    try {
      await cancelIngest(collectionId)
      toast.info(t('ingest.cancelling'))
      qc.invalidateQueries({ queryKey: ['ingest', collectionId] })
    } catch (e) {
      toast.error(apiErrorMessage(e))
    }
  }, [collectionId, qc, setUploads, t])

  const dismiss = useCallback(() => {
    if (collectionId !== null) clearUploads(collectionId)
    qc.setQueryData(['ingest', collectionId], { status: 'idle', files: [] })
  }, [collectionId, qc])

  return {
    status: status.data,
    uploads,
    byDocument,
    sending,
    running,
    busy: sending || running,
    visible:
      sending ||
      running ||
      uploads.length > 0 ||
      status.data?.status === 'done' ||
      status.data?.status === 'cancelled',
    start,
    stop,
    dismiss,
  }
}

export type Ingest = ReturnType<typeof useIngest>

/**
 * One line of summary above the list. Deliberately not a second file list —
 * the files themselves are the rows below, which is where the user is already
 * looking; repeating them here would be two places to watch for one thing.
 */
export function IngestBar({
  ingest,
  canEdit,
  collectionId,
  unchecked = 0,
  onShowTerms,
}: {
  ingest: Ingest
  canEdit: boolean
  collectionId: number
  /** Terms no build has judged yet — what a run would actually work on. */
  unchecked?: number
  /** Switch to the terms view, where the concept build reports its progress. */
  onShowTerms?: () => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [building, setBuilding] = useState(false)
  const { status, sending, running, uploads } = ingest
  const pct = status?.percentage ?? 0
  const queued = uploads.filter(
    (u) => u.status === 'checking' || u.status === 'waiting' || u.status === 'sending',
  ).length

  const active = status?.files?.find((f) => f.status === 'running')
  const label = sending
    ? t('ingest.uploadingCount', { count: queued })
    : running
      ? t('ingest.progress', { current: status?.current ?? 0, total: status?.total ?? 0 })
      : t('ingest.finished')

  // With no batch in this page's history there is nothing to summarise, and
  // "Files processed" would be a claim about something that did not happen —
  // the bar is then only a carrier for the recommendation below.
  const hasBatch = ingest.visible

  return (
    <div className="border-b border-border bg-card px-3 py-2">
      {hasBatch && (
      <div className="flex items-center gap-2">
        <p className="flex min-w-0 flex-1 items-center gap-1.5 text-xs font-medium">
          {(sending || running) && <Loader2 className="size-3.5 shrink-0 animate-spin text-primary" />}
          <span className="truncate">{label}</span>
        </p>
        {running && !sending && (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{pct}%</span>
        )}
        {sending || running ? (
          canEdit && (
            <Button variant="outline" size="sm" onClick={ingest.stop}>
              {t('common.cancel')}
            </Button>
          )
        ) : (
          <Button variant="ghost" size="icon-sm" title={t('common.close')} onClick={ingest.dismiss}>
            <X />
          </Button>
        )}
      </div>
      )}
      {running && !sending && (
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {/* Extraction produces terms; terms are only useful once grouped into
          concepts, and nothing else in this view says so. The moment a batch
          finishes is exactly when that is the next thing to do — and the only
          moment the user is definitely looking at this pane. */}
      {!sending && !running && !!unchecked && canEdit && (
        <div
          className={cn(
            'flex items-center gap-2 rounded border border-border bg-muted/50 p-2',
            hasBatch && 'mt-2',
          )}
        >
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-medium">{t('ingest.nextStep')}</p>
            <p className="text-[11px] text-muted-foreground">
              {t('ingest.nextStepHint', { count: unchecked })}
            </p>
          </div>
          <Button
            size="sm"
            disabled={building}
            onClick={async () => {
              setBuilding(true)
              try {
                await startConceptBuild(collectionId)
                qc.invalidateQueries({ queryKey: ['concept-build', collectionId] })
                // The build reports progress in the terms view, so go where it
                // can be watched rather than starting it out of sight.
                onShowTerms?.()
                ingest.dismiss()
              } catch (e) {
                toast.error(apiErrorMessage(e))
              } finally {
                setBuilding(false)
              }
            }}
          >
            {building ? <Loader2 className="animate-spin" /> : <Boxes />}
            {t('ingest.buildConcepts')}
          </Button>
        </div>
      )}

      {running && !sending && (
        <p className="mt-1 truncate text-[11px] text-muted-foreground">
          {/* Which file, then which step of it. "file 3 of 21" says how far
              along the batch is but never says what it is chewing on. */}
          {active && <span className="font-medium text-foreground">{active.title}</span>}
          {active && status?.message ? ' — ' : ''}
          {status?.message}
        </p>
      )}
    </div>
  )
}

/**
 * A file that is still in the browser: queued to send, or going up now.
 *
 * Shaped like a real document row and sitting in the same list, so a batch
 * reads as one continuous sequence — files become documents in place rather
 * than disappearing from one list and reappearing in another.
 */
export function UploadingRow({ row }: { row: UploadRow }) {
  const { t } = useTranslation()
  const failed = row.status === 'failed'
  const skipped = row.status === 'skipped'
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 border-b border-border/60 px-3 py-2',
        !failed && !skipped && 'opacity-70',
      )}
    >
      <span className="shrink-0">
        {failed ? (
          <AlertTriangle className="size-4 text-danger" />
        ) : skipped ? (
          <Minus className="size-4 text-muted-foreground" />
        ) : row.status === 'sending' || row.status === 'checking' ? (
          <Loader2 className="size-4 animate-spin text-primary" />
        ) : (
          <FileText className="size-4 text-muted-foreground/60" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'truncate text-sm',
            (failed || skipped) && 'text-muted-foreground',
            skipped && 'line-through',
          )}
          title={row.name}
        >
          {row.name}
        </p>
        <p className="truncate text-[11px] text-muted-foreground">
          {failed
            ? row.message || t('ingest.uploadFailed')
            : skipped
              ? row.duplicateOf
                ? t('ingest.duplicateOf', { title: row.duplicateOf })
                : t('ingest.duplicate')
              : row.status === 'checking'
                ? t('ingest.checking')
                : row.status === 'sending'
                  ? t('ingest.sendingPct', { pct: row.percentage })
                  : t('ingest.waiting')}
        </p>
        {row.status === 'sending' && (
          <div className="mt-1 h-0.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${row.percentage}%` }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

/** The drop target itself, in bulk mode. */
export function BulkDrop({
  onFiles,
  busy,
  compact,
}: {
  onFiles: (files: File[]) => void
  busy: boolean
  compact?: boolean
}) {
  const { t } = useTranslation()
  const [over, setOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const depth = useRef(0)

  const take = (list: FileList | null) => {
    const pdfs = Array.from(list ?? []).filter(
      (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'),
    )
    if (pdfs.length) onFiles(pdfs)
    else if (list?.length) toast.error(t('ingest.pdfOnly'))
  }

  return (
    <div
      onDragEnter={(e) => {
        e.preventDefault()
        depth.current += 1
        setOver(true)
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        depth.current -= 1
        if (depth.current <= 0) setOver(false)
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        depth.current = 0
        setOver(false)
        if (!busy) take(e.dataTransfer.files)
      }}
      onClick={() => !busy && inputRef.current?.click()}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed transition-colors',
        compact ? 'p-4' : 'p-6',
        over ? 'border-primary bg-accent' : 'border-border bg-card hover:border-ring hover:bg-muted',
        busy && 'pointer-events-none opacity-60',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          take(e.target.files)
          e.target.value = ''
        }}
      />
      <FileText className={cn('size-6', over ? 'text-primary' : 'text-muted-foreground')} />
      <div className="text-center">
        <p className="text-sm font-medium">{t('ingest.dropLabel')}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('ingest.dropHint')}</p>
      </div>
    </div>
  )
}
